import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Cpu, Image as ImageIcon, ShieldCheck, LogOut, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Scroller, Spinner } from "@/pages/_shared";

export default function Settings() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get("/ai/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);

  if (!status) return <Spinner />;
  const chat = status.chat;
  const image = status.image;

  return (
    <Scroller>
      <PageHeader title="Settings" subtitle="Account and BDVG AI infrastructure" icon={SettingsIcon} />

      <Card icon={ShieldCheck} title="Account">
        <Row label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={user?.role} />
        <button onClick={logout} className="mt-3 flex items-center gap-2 rounded-xl border border-destructive/30 px-4 py-2 text-sm text-red-300 hover:bg-destructive/10" data-testid="settings-logout">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </Card>

      <Card icon={Cpu} title="BDVG AI Model">
        <div className="mb-3 flex items-center gap-2 text-sm">
          {chat.available ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
          <span className={chat.available ? "text-emerald-300" : "text-amber-300"}>{chat.available ? "Online" : chat.message}</span>
        </div>
        <Row label="Mode" value={chat.mode} />
        <Row label="Model" value={chat.label} />
        <p className="mt-3 text-xs leading-relaxed text-white/40">
          BDVG AI runs on a model-agnostic, self-hosted engine. In <b>embedded</b> mode a self-hosted open-weight
          model runs in-process. Set <code className="text-gold/70">DIAMOND_LLM_MODE=remote</code> with
          <code className="text-gold/70"> DIAMOND_LLM_BASE_URL</code> (an OpenAI-compatible endpoint such as vLLM) to
          connect your own GPU-hosted model. No third-party commercial provider is ever used as the core intelligence.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {chat.categories.map((c) => (
            <span key={c.id} className="rounded-full border border-gold/20 bg-gold/5 px-3 py-1 text-xs text-gold/80">{c.label}</span>
          ))}
        </div>
      </Card>

      <Card icon={ImageIcon} title="Image Engine">
        <div className="flex items-center gap-2 text-sm">
          {image.available ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
          <span className={image.available ? "text-emerald-300" : "text-amber-300"}>{image.available ? "Online" : image.message}</span>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-white/40">
          Connect a self-hosted image model (FLUX / SDXL) via <code className="text-gold/70">DIAMOND_IMAGE_BASE_URL</code> to enable generation.
        </p>
      </Card>
    </Scroller>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="mb-4 rounded-2xl border border-white/8 bg-charcoal p-5">
      <div className="mb-4 flex items-center gap-2.5"><Icon className="h-5 w-5 text-gold" /><h2 className="font-heading text-lg font-semibold">{title}</h2></div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-white/5 py-2 text-sm last:border-0">
      <span className="text-white/45">{label}</span>
      <span className="font-medium text-white/85">{value}</span>
    </div>
  );
}
